import { useEffect, useRef } from 'react'
import {
  getElementTextDirection,
  getLogicalInlineCenterPosition,
  scrollByLogicalPosition,
} from 'lib/dom/logicalScroll'

type ScrollCondition = 'always' | 'outOfView'

const shouldScrollByCondition: Record<ScrollCondition, (scrollerRect: DOMRect, contentElementRect: DOMRect) => boolean> = {
  always: () => true,
  outOfView: (scrollerRect, contentElementRect) => {
    return contentElementRect.right > scrollerRect.right || contentElementRect.left < scrollerRect.left
  },
}

export default function useHorizontalScrollIntoView(
  selected?: string,
  options?: { scrollCondition?: ScrollCondition },
): [
  scroller: React.MutableRefObject<HTMLDivElement | null>,
  contentElements: React.MutableRefObject<{ [x: string]: HTMLElement }>,
] {
  const scroller = useRef<HTMLDivElement | null>(null)
  const stepElements = useRef<{ [x: string]: HTMLElement }>({})

  const scrollCondition = options?.scrollCondition || 'always'

  useEffect(() => {
    const scrollerELement = scroller.current
    if (scrollerELement && selected) {
      const scrollerRect = scrollerELement.getBoundingClientRect()
      const contentElement = stepElements.current[selected]
      const contentElementRect = contentElement?.getBoundingClientRect()
      const shouldScroll = shouldScrollByCondition[scrollCondition]

      if (scrollerRect && contentElementRect && scrollerELement.scrollWidth > scrollerRect.width && shouldScroll(scrollerRect, contentElementRect)) {
        // Adjust scroll to centre the element
        const textDirection = getElementTextDirection(scrollerELement)
        const currentCentre = getLogicalInlineCenterPosition(contentElement, textDirection)
        const targetCentre = getLogicalInlineCenterPosition(scrollerELement, textDirection)
        const scrollPosition = currentCentre - targetCentre
        scrollByLogicalPosition(scrollerELement, { left: scrollPosition, behavior: 'smooth' })
      }
    }
  }, [scrollCondition, selected])

  return [scroller, stepElements]
}
